import { invoke } from '@shared/easyfy.decorators';
import { elements as getElements } from '@shared/easyfy.core';
import Splide from '@splidejs/splide';
import { Grid } from '@splidejs/splide-extension-grid';
import { isMobile } from '@clientShared/utils';
import SliderHelper from '@clientShared/sliderHelper';

@invoke
export default class ProductListComponent {
  static Instance: ProductListComponent;

  constructor() {
    ProductListComponent.Instance = this;
  }

  onInit() {
    ProductListComponent.refreshProductList();

    window.addEventListener('component-productlistupdate', (() => {
      ProductListComponent.refreshProductList();
    }) as EventListener);
  }

  static refreshProductList = () => {
    getElements('[data-container="productlist-scroll"]').forEach((list) => {
      list.classList.add('splide');
      const columnsDesktop: number = parseInt((list as HTMLElement).getAttribute('data-columns-d'), 0);
      const columnsTablet: number = parseInt((list as HTMLElement).getAttribute('data-columns-t'), 0);
      const columnsMobile: number = parseInt((list as HTMLElement).getAttribute('data-columns-m'), 0);

      const gapDesktop: number = 32;
      const gapTablet: number = 24;
      const gapMobile: number = 12;

      const paddingRightDesktop: number = 0;
      const paddingRightTablet: number = columnsTablet > 1 ? 0 : 50;
      const paddingRightMobile: number = columnsMobile > 1 ? 0 : 200;

      const cardWidthDesktop: number = ProductListComponent.Instance.calculateCardWidth(
        list as HTMLElement,
        isNaN(columnsDesktop) ? 0 : columnsDesktop,
        gapDesktop,
        paddingRightDesktop
      );
      const cardWidthTablet: number = ProductListComponent.Instance.calculateCardWidth(
        list as HTMLElement,
        isNaN(columnsTablet) ? 0 : columnsTablet,
        gapTablet,
        paddingRightTablet
      );
      const cardWidthMobile: number = ProductListComponent.Instance.calculateCardWidth(
        list as HTMLElement,
        isNaN(columnsMobile) ? 0 : columnsMobile,
        gapMobile,
        paddingRightMobile
      );

      const slider = new Splide(list as HTMLElement, {
        type: 'loop',
        perMove: 1,
        speed: 300,
        dragMinThreshold: 20,
        flickMaxPages: 1,
        flickPower: 100,
        snap: false,
        arrows: true,
        gap: gapDesktop,
        fixedWidth: cardWidthDesktop,
        padding: {
          left: 0,
          right: 0,
        },
        breakpoints: {
          1100: {
            gap: gapTablet,
            arrows: columnsTablet > 1,
            fixedWidth: cardWidthTablet,
          },
          768: {
            gap: gapMobile,
            arrows: columnsMobile > 1,
            fixedWidth: cardWidthMobile,
          },
        },
      });

      SliderHelper.OnPaginationMounted(slider, list as HTMLElement);

      slider.mount();

      SliderHelper.AddSliderOverlay(list as HTMLElement);
      SliderHelper.UpdateImagesSize(list as HTMLElement);
      SliderHelper.removeDuplicatedLinks(slider);
    });

    getElements('[data-container="productlist-scroll-compact"]').forEach((list) => {
      list.classList.add('splide');
      const slider = new Splide(list as HTMLElement, {
        type: 'loop',
        focus: 0,
        perPage: 3.5,
        speed: 300,
        dragMinThreshold: 20,
        flickMaxPages: 1,
        flickPower: 100,
        pagination: true,
        snap: false,
        arrows: true,
        gap: 16,
        padding: {
          left: 2,
          right: 0,
        },
        breakpoints: {
          768: {
            arrows: false,
            perPage: 1,
            perMove: 1,
            padding: {
              left: 0,
              right: 0,
            },
            fixedHeight: 200,
            grid: {
              rows: 3,
              gap: {
                row: '16px',
                col: '',
              },
            },
          },
        },
      });

      slider.on('pagination:mounted', function (data) {
        // if fewer slides than provided in options, hide pagination
        if (data.items.length <= slider.options.perPage) {
          slider.options.type = 'slide';
          slider.options = {
            pagination: false,
          };
          if (slider.Components.Arrows.arrows.prev) slider.Components.Arrows.arrows.prev.style.display = 'none';
          if (slider.Components.Arrows.arrows.next) slider.Components.Arrows.arrows.next.style.display = 'none';
        }
        if (isMobile()) data.list.classList.add('with-dots');
        if (!isMobile()) data.list.classList.add('with-lines');
      });
      slider.mount({ Grid });

      if (!isMobile()) {
        SliderHelper.AddSliderOverlay(list as HTMLElement);
      }
    });
  };

  private calculateCardWidth = (list: HTMLElement, columns: number, gap: number, paddingRight: number) => {
    if (columns <= 1) {
      const totalGapWidth = paddingRight > 0 ? gap : 0;
      const paddingRightForEachCard = paddingRight / 2;

      return list.clientWidth - paddingRightForEachCard + totalGapWidth;
    }

    const totalGapWidth = gap * (columns - 1);
    const paddingRightForEachCard = paddingRight / columns;

    return (list.clientWidth - totalGapWidth) / columns + paddingRightForEachCard;
  };
}
